// One shared status -> colour map for background-process / job statuses.
// Stop-light rule: green = good, amber = caution, red = bad.
// `tag` is an antd Tag preset colour; `chart` is the hex used for chart series (e.g. the Dashboard pie).

export interface IStatusColor {
  tag: string;
  chart: string;
}

const CAUTION_AMBER: IStatusColor = { tag: 'orange', chart: '#fa8c16' };

// Keys are lower-case; look up through getStatusColor() so matching is case-insensitive.
export const STATUS_COLORS: Readonly<Record<string, IStatusColor>> = {
  completed: { tag: 'green', chart: '#52c41a' },
  error: { tag: 'red', chart: '#f5222d' },
  aborted: CAUTION_AMBER,
  hang: CAUTION_AMBER,
  cancelled: { tag: 'purple', chart: '#b37feb' }, // light purple, clearly distinct from Ready grey
  running: { tag: 'blue', chart: '#1890ff' },
  ready: { tag: 'default', chart: '#bfbfbf' }, // queued: light grey
};

// Any status not listed above: a distinct neutral that is neither grey nor purple.
export const NEUTRAL_STATUS_COLOR: IStatusColor = { tag: 'cyan', chart: '#13c2c2' };

export const getStatusColor = (status?: string | null): IStatusColor => {
  const key = (status ?? '').toString().trim().toLowerCase();
  return STATUS_COLORS[key] ?? NEUTRAL_STATUS_COLOR;
};

export const getStatusTagColor = (status?: string | null): string => getStatusColor(status).tag;

export const getStatusChartColor = (status?: string | null): string =>
  getStatusColor(status).chart;

// Returns copies of chart data points (`{ name, ... }`, name = status) with an ECharts
// itemStyle colour from the map, so colours follow the status name, not the slice order.
export const withStatusChartColors = <T extends { name?: string | null }>(
  data: ReadonlyArray<T>
): Array<T & { itemStyle: { color: string } }> =>
  data.map((point) => ({ ...point, itemStyle: { color: getStatusChartColor(point.name) } }));
